<template>
  <form @submit.prevent="onSubmit">
    <div class="flex items-baseline mb-2">
      <label
        for="username"
        class="block px-0.5 mb-1 text-sm font-medium select-none text-gray-800/70"
        >Username
      </label>
      <fa-icon
        v-if="!hideCancel && !showEdit"
        icon="pencil"
        class="ml-2 text-xs cursor-pointer text-gray-800/70 hover:text-gray-800 transition-colors"
        title="Edit username"
        @click.prevent="toggleEditMode" />
      <span
        v-if="!hideCancel && showEdit"
        class="ml-2 text-xs cursor-pointer hover:underline text-gray-800/70 select-none"
        @click.prevent="toggleEditMode">
        cancel
      </span>
    </div>

    <div class="flex items-center space-x-2">
      <div
        class="flex overflow-hidden flex-1 items-center px-1 space-x-2 rounded-md border-0 border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        :class="[
          showEdit
            ? 'bg-white/40 text-gray-800/90'
            : 'bg-white/40 text-blue-500'
        ]">
        <input
          id="username"
          v-model="username"
          type="text"
          autocomplete="username"
          :readonly="!showEdit"
          :disabled="!showEdit"
          class="block w-full text-sm font-bold bg-transparent border-none ring-0" />

        <!-- Random username button -->
        <button
          v-if="showEdit && showRandomOption"
          type="button"
          :disabled="isLoadingSuggestions"
          class="flex items-center cursor-pointer justify-center w-7 h-7 text-gray-500 rounded-md shrink-0 hover:text-indigo-600 hover:bg-white/50 transition-colors"
          :class="{ 'opacity-50 cursor-wait': isLoadingSuggestions }"
          title="Generate random username"
          @click.prevent="cycleRandomUsername">
          <fa-icon
            :icon="isLoadingSuggestions ? 'spinner-third' : 'shuffle'"
            :spin="isLoadingSuggestions"
            class="text-sm" />
        </button>

        <template v-if="showEdit && !autoEdit">
          <button
            v-if="!formBusy"
            type="submit"
            class="px-4 h-7 font-bold text-white bg-indigo-600 rounded-md cursor-pointer text-2xs hover:bg-indigo-500"
            @click.prevent="onSubmit">
            Update
          </button>
          <button
            v-else
            class="px-4 w-20 h-7 text-xs font-bold text-white bg-gray-400 rounded-md cursor-not-allowed">
            <fa-icon icon="spinner-third" spin />
          </button>
        </template>
      </div>
    </div>

    <div
      v-if="formError"
      class="p-2 mt-2 text-xs italic text-red-600 rounded-md cursor-pointer select-none hover:bg-white/20"
      @click.prevent="formError = null">
      <p class="font-bold">
        <fa-icon class="mr-1" icon="triangle-exclamation" />
        <span v-if="formError.message">{{ formError.message }}</span>
        <span v-else>{{ formError }}</span>
      </p>
    </div>
  </form>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useStoreProfile } from '@/stores/storeProfile'
import { useStoreAuth } from '@/stores/storeAuth'
import { supabase } from '@/supabase'

const storeProfile = useStoreProfile()
const storeAuth = useStoreAuth()

const props = defineProps({
  profile: Object,
  autoEdit: {
    type: Boolean,
    default: false
  },
  hideCancel: {
    type: Boolean,
    default: false
  },
  showRandomOption: {
    type: Boolean,
    default: false
  }
})

const showEdit = ref(false)
const username = ref('')
const formBusy = ref(false)
const formError = ref(null)

// Random username state
const isLoadingSuggestions = ref(false)
const suggestions = ref([])
const currentSuggestionIndex = ref(0)

onMounted(() => {
  username.value = props.profile?.username
  // Auto-enable edit mode if autoEdit prop is true
  if (props.autoEdit) {
    showEdit.value = true
  }
})

const toggleEditMode = () => {
  if (showEdit.value) {
    cancelEditMode()
  } else {
    showEdit.value = !showEdit.value
    formError.value = null
  }
}

const cancelEditMode = () => {
  showEdit.value = false
  formError.value = null
  username.value = props.profile?.username
  // Reset suggestions when cancelling
  suggestions.value = []
  currentSuggestionIndex.value = 0
}

const updateUsername = () => {
  formBusy.value = true
  storeProfile
    .updateUsername(username.value)
    .then(() => {
      formBusy.value = false
      toggleEditMode()
    })
    .catch((error) => {
      formBusy.value = false
      formError.value = error
    })
}

const onSubmit = (e) => {
  e.preventDefault()

  if (username.value !== props.profile?.username) {
    updateUsername()
  } else {
    toggleEditMode()
  }
}

// Fetch username suggestions from the server
const fetchSuggestions = async () => {
  isLoadingSuggestions.value = true
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) return

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/username-suggestions`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ count: 5 })
      }
    )

    if (!response.ok) {
      throw new Error('Failed to fetch suggestions')
    }

    const data = await response.json()
    suggestions.value = data.suggestions || []
    currentSuggestionIndex.value = 0
  } catch (error) {
    console.error('Error fetching username suggestions:', error)
    suggestions.value = []
  } finally {
    isLoadingSuggestions.value = false
  }
}

// Cycle through random usernames
const cycleRandomUsername = async () => {
  // If we have no suggestions or have cycled through all, fetch new ones
  if (
    suggestions.value.length === 0 ||
    currentSuggestionIndex.value >= suggestions.value.length
  ) {
    await fetchSuggestions()
  }

  // Apply the current suggestion
  if (suggestions.value.length > 0) {
    username.value = suggestions.value[currentSuggestionIndex.value]
    currentSuggestionIndex.value++
  }
}

// Function to save without toggling edit mode (for onboarding)
const saveForOnboarding = async () => {
  if (!props.autoEdit || username.value === props.profile?.username) return

  try {
    formBusy.value = true
    await storeProfile.updateUsername(username.value)
    formBusy.value = false
  } catch (error) {
    formBusy.value = false
    formError.value = error
    throw error
  }
}

// Expose methods for parent component
defineExpose({
  saveForOnboarding,
  hasChanges: () => username.value !== props.profile?.username,
  cycleRandomUsername
})
</script>
